import {
  Box,
  Divider,
  Stack,
  Typography,
  useMobileBreakpoint
} from '@customink/pigment-react';
import {styled} from '@customink/pigment-react/lib/themeCustomink';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import ProductFeatureBadges from './components/Badges/ProductFeatureBadges';
import ProductPromoBadges from './components/Badges/ProductPromoBadges';
import ElevateSuperRush from './components/ElevateSuperRush';
import MinQty from './components/MinQty';
import OutOfStockBanner from './components/OutOfStockBanner';
import OutOfStockOverlay from './components/OutOfStockOverlay';
import ProductCardColorSwatches from './components/ProductCardColorSwatches';
import ProductCardPriceDetails from './components/ProductCardPriceDetails/ProductCardPriceDetails';
import ProductCardRating from './components/ProductCardRating';
import ProductCardSizesInfo from './components/ProductCardSizesInfo';
import useProductCardLink from './hooks/useProductCardLink';
import {getMinimumDeliveryDays} from './utils/delivery';

const ProductImage = styled('img', {
  shouldForwardProp: (prop) => prop !== 'cover'
})(({cover = false}) => ({
  '@media (min-width: 43.75em)': {
    aspectRatio: '1 / 1',
    maxHeight: 'unset'
  },
  maxHeight: '250px',
  minHeight: 120,
  objectFit: 'contain',
  order: 2,
  transitionDuration: '150ms',
  width: '100%',
  ...(cover && {
    aspectRatio: '1',
    objectFit: 'cover',
    objectPosition: 'top'
  })
}));

// Extracted styled components to prevent recreation on each render
const ProductImageContainer = styled(Box)({
  '&:before': {
    boxShadow: '0px 0px 60px 24px #f5f5f5 inset',
    content: '""',
    inset: 0,
    mixBlendMode: 'darken',
    position: 'absolute'
  },
  '@media (min-width: 43.75em)': {
    flexBasis: 'unset',
    width: '100%'
  },
  borderRadius: '0.5rem',
  display: 'flex',
  flexBasis: '40%',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
  transitionDuration: '150ms'
});

const ProductCardContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'isMobile'
})(({isMobile}) => ({
  ...(!isMobile && {
    '&:hover': {
      backgroundColor: 'white',
      boxShadow:
        '0px 0px 0px 1px rgba(0, 0, 0, 0.06),0px 8px 16px -4px rgba(0, 0, 0, 0.15)',
      margin: '-0.75rem',
      padding: '0.75rem',
      zIndex: 1
    }
  }),
  alignItems: 'flex-start',
  backgroundColor: 'transparent',
  borderRadius: '0.5rem',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: isMobile ? 'row' : 'column',
  gap: isMobile ? '0.75rem' : '0.5rem',
  margin: '-0.375rem',
  padding: '0.375rem',
  position: 'relative',
  transitionDuration: '150ms',
  WebkitTapHighlightColor: 'transparent'
}));

// Visually hidden link for SEO and accessibility
const HiddenProductLink = styled('a')({
  height: '100%',
  left: 0,
  opacity: 0,
  pointerEvents: 'none',
  position: 'absolute',
  top: 0,
  width: '100%',
  zIndex: 1
});

const ProductTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'compact'
})(({compact = false}) => ({
  color: '#000000db',
  fontSize: '0.875rem',
  fontWeight: 'bold',
  lineHeight: '1.25',
  marginBottom: '-0.15em',
  marginTop: compact ? 'unset' : '-0.155em'
}));

// this is a Placeholder for the Product Card
export default function ProductCard({
  backColor,
  badges,
  defaultUnitPrices,
  displayAllSwatches = false,
  displayImageAsCover = false,
  frontColor,
  generalComments,
  getProductImageSrcData,
  initialSelectedColorId,
  minQty,
  name,
  noQtyLimit,
  productColors,
  productId,
  productPath,
  quantity,
  ratingCount,
  ratingScore,
  rushDeliveryTurntimes,
  singlesEnabledColors,
  sizes,
  specialty,
  status
}) {
  const isMobile = useMobileBreakpoint();
  const [selectedColorId, setSelectedColorId] = useState(
    initialSelectedColorId || null
  );
  const [productImage, setProductImage] = useState(() =>
    getProductImageSrcData(selectedColorId)
  );

  useEffect(() => {
    setProductImage(getProductImageSrcData(selectedColorId));
  }, [selectedColorId, getProductImageSrcData]);

  useEffect(() => {
    setSelectedColorId(initialSelectedColorId || null);
  }, [productColors, initialSelectedColorId]);

  const linkHref = useProductCardLink({
    badges,
    initialSelectedColorId,
    productPath,
    selectedColorId
  });

  const minDeliveryDays = useMemo(
    () => getMinimumDeliveryDays(rushDeliveryTurntimes),
    [rushDeliveryTurntimes]
  );

  const onSwatchClick = useCallback(
    (swatch) => {
      setSelectedColorId(Number(swatch.value));
    },
    [setSelectedColorId]
  );

  const onSwatchHover = useCallback(
    (swatch) => {
      setSelectedColorId(Number(swatch.value));
    },
    [setSelectedColorId]
  );

  const onProductCardClick = useCallback(() => {
    window.location.href = linkHref;
  }, [linkHref]);

  const isOutOfStock = status === 'inactive';

  // Memoize the swatches component to prevent unnecessary re-renders
  const swatchesComponent = useMemo(
    () => (
      <Stack direction="row" alignItems="flex-start">
        <ProductCardColorSwatches
          onSwatchClick={onSwatchClick}
          onSwatchHover={onSwatchHover}
          productColors={productColors}
          displayAllSwatches={displayAllSwatches}
        />
      </Stack>
    ),
    [onSwatchClick, onSwatchHover, productColors, displayAllSwatches]
  );

  return (
    <ProductCardContainer
      isMobile={isMobile}
      onClick={onProductCardClick}
      position="relative">
      {/* Hidden anchor tag for SEO */}
      <HiddenProductLink
        href={linkHref}
        aria-label={`View details for ${name}`}
        tabIndex="-1"
      />

      {isOutOfStock && <OutOfStockOverlay />}
      {/* Product Image */}
      <ProductImageContainer>
        {isOutOfStock && <OutOfStockBanner />}
        <ProductImage
          cover={displayImageAsCover}
          src={productImage.src}
          srcSet={productImage.srcset}
          alt={name}
        />

        {/* Badges */}
        <ProductPromoBadges badges={badges} compact={isMobile} />
        <ProductFeatureBadges
          badges={badges}
          compact={isMobile}
          productName={name}
        />
      </ProductImageContainer>

      {/* Product Details */}
      <Stack gap="0.5rem" width={isMobile ? '60%' : '100%'} flex={1}>
        {/* Conditionally render components based on viewport */}
        {isMobile && <ProductTitle compact>{name}</ProductTitle>}

        {/* Limited number of swatches */}
        {!displayAllSwatches && swatchesComponent}

        {/* Product Title - desktop only (after swatches) */}
        {!isMobile && <ProductTitle>{name}</ProductTitle>}

        {/* Rating and Sizes  */}
        <Stack direction="row" gap="0.5rem" alignItems="center">
          <ProductCardRating
            count={ratingCount}
            score={ratingScore}
            generalComments={generalComments}
            name={name}
          />
          <Divider orientation="vertical" flexItem />
          <ProductCardSizesInfo productId={productId} sizes={sizes} />
        </Stack>

        {/* Price and Details */}
        <ProductCardPriceDetails
          backColor={backColor}
          defaultUnitPrices={defaultUnitPrices}
          frontColor={frontColor}
          isMobile={isMobile}
          minQty={minQty}
          productId={productId}
          name={name}
          quantity={quantity}
          singlesEnabled={singlesEnabledColors}
          specialty={specialty}
        />

        {/* Delivery Rush Badge */}
        {minDeliveryDays && (
          <ElevateSuperRush deliveryRushDays={minDeliveryDays} />
        )}

        {/* Minimum qty */}
        <MinQty minQty={noQtyLimit ? null : minQty} />

        {/* All Swatches */}
        {displayAllSwatches && swatchesComponent}
      </Stack>
    </ProductCardContainer>
  );
}

ProductCard.propTypes = {
  backColor: PropTypes.number.isRequired,
  badges: PropTypes.array,
  defaultUnitPrices: PropTypes.arrayOf(PropTypes.object).isRequired,
  displayAllSwatches: PropTypes.bool,
  displayImageAsCover: PropTypes.bool,
  frontColor: PropTypes.number.isRequired,
  generalComments: PropTypes.string,
  getProductImageSrcData: PropTypes.func,
  initialSelectedColorId: PropTypes.number,
  minQty: PropTypes.number,
  name: PropTypes.string.isRequired,
  noQtyLimit: PropTypes.bool,
  productColors: PropTypes.array.isRequired,
  productId: PropTypes.number.isRequired,
  productPath: PropTypes.string.isRequired,
  quantity: PropTypes.number.isRequired,
  ratingCount: PropTypes.number,
  ratingScore: PropTypes.number,
  rushDeliveryTurntimes: PropTypes.array,
  sizes: PropTypes.array,
  specialty: PropTypes.bool.isRequired,
  status: PropTypes.string.isRequired
};
